import { useStoreKeySelector } from '@/lib/valtio/store-hooks'

import type { Column } from '~shared/rundown/column'

import {
  hasRundownPermission,
  useRundownAccess,
} from '@/features/permissions/lib/use-rundown-access'
import { userStore } from '@/features/user/user.store'

import { getColumnAccess } from './column-access.rules'
import type { ColumnAccess } from './column-access.rules'
import { columnShareLinkReadonly } from './column-share-link'

/**
 * The viewer's access to one column, for React callers.
 *
 * @remarks
 *   Subscribes to the user id and to the share link's editable ids, then lets
 *   `getColumnAccess` decide. Permissions come from the rundown token, which is
 *   read at render like everywhere else.
 *   The editable ids are selected as the whole list, not per column, so the
 *   context `getColumnAccess` takes stays plain. Its cost: every header
 *   re-renders when the list is replaced, which only happens when the session
 *   starts or stops.
 */
export function useColumnAccess(
  column: Pick<Column, 'id' | 'privateUid'>,
): ColumnAccess {
  const userId = useStoreKeySelector(userStore, 'userId', (id) => id)
  const editableColumnIds = useStoreKeySelector(
    columnShareLinkReadonly,
    'editableColumns',
    (ids) => ids,
  )
  const { hasPermission } = useRundownAccess()

  return getColumnAccess(column, { userId, hasPermission, editableColumnIds })
}

/**
 * The viewer's access to one column, read from the stores at call time.
 *
 * For event handlers and for hooks that deliberately do not subscribe. Same
 * rules as `useColumnAccess`.
 */
export function readColumnAccess(
  column: Pick<Column, 'id' | 'privateUid'>,
): ColumnAccess {
  return getColumnAccess(column, {
    userId: userStore.userId,
    hasPermission: hasRundownPermission,
    editableColumnIds: columnShareLinkReadonly.editableColumns,
  })
}
